import { DocumentSnapshot, SnapshotOptions } from 'firebase/firestore';
import { SupportedLocale } from '../services/locale-switch.service';

export type Role = 'teacher' | 'parent' | 'student';
export type UserId = string;

export class User {
  id: UserId; // snapshot.id, not literally saved in document
  displayName?: string;
  role?: Role;
  email?: string;
  phoneNumber?: string;
  photoURL?: string;
  publiclyVisible?: boolean;
  rewardPointTotal?: number = 0; // use firebase incrementing in user service
  canWriteAttributeGroups = false;
  parentMail?: string;
  sendMailNotification?: boolean = false;
  sendDailyMailSummary?: boolean = false;
  sendWeeklyMailSummary?: boolean = false;
  sendPrepareStartEmail?: boolean = false;
  sendPrepareResumeEmail?: boolean = false;
  sendPrepareFinishEmail?: boolean = false;
  sendStudyStartEmail?: boolean = false;
  sendStudyFinishEmail?: boolean = false;
  sendVerifyStartEmail?: boolean = false;
  sendVerifyResumeEmail?: boolean = false;
  sendVerifyFinishEmail?: boolean = false;
  sendOnDeckStart?: boolean;
  sendOnDeckEnd?: boolean;
  tourCompleted?: boolean = false;
  preferredLanguage?: SupportedLocale;

  // a user can have the following sub collections
  // decks?: Deck[]; // this collection will be "learned", other decks will always be copied here
  // userGroup?: userGroups[];

  constructor(
    id: UserId,
    displayName: string | undefined,
    role: Role | undefined,
    email: string | undefined,
    phoneNumber: string | undefined,
    photoURL: string | undefined,
    publiclyVisible: boolean = false,
    rewardPointTotal: number = 0,
    canWriteAttributeGroups = false,
    parentMail: string | undefined = undefined,
    sendMailNotification: boolean = false,
    sendDailyMailSummary: boolean = false,
    sendWeeklyMailSummary: boolean = false,
    sendPrepareStartEmail: boolean = sendMailNotification,
    sendPrepareResumeEmail: boolean = sendMailNotification,
    sendPrepareFinishEmail: boolean = sendMailNotification,
    sendStudyStartEmail: boolean = sendMailNotification,
    sendStudyFinishEmail: boolean = sendMailNotification,
    sendVerifyStartEmail: boolean = sendMailNotification,
    sendVerifyResumeEmail: boolean = sendMailNotification,
    sendVerifyFinishEmail: boolean = sendMailNotification,
    tourCompleted: boolean = false,
    preferredLanguage?: SupportedLocale
  ) {
    this.id = id;
    this.displayName = displayName;
    this.role = role;
    this.email = email;
    this.phoneNumber = phoneNumber;
    this.photoURL = photoURL;
    this.publiclyVisible = publiclyVisible;
    this.rewardPointTotal = rewardPointTotal;
    this.canWriteAttributeGroups = canWriteAttributeGroups;
    this.parentMail = parentMail;
    this.sendMailNotification = sendMailNotification;
    this.sendDailyMailSummary = sendDailyMailSummary;
    this.sendWeeklyMailSummary = sendWeeklyMailSummary;
    this.sendPrepareStartEmail = sendPrepareStartEmail;
    this.sendPrepareResumeEmail = sendPrepareResumeEmail;
    this.sendPrepareFinishEmail = sendPrepareFinishEmail;
    this.sendStudyStartEmail = sendStudyStartEmail;
    this.sendStudyFinishEmail = sendStudyFinishEmail;
    this.sendVerifyStartEmail = sendVerifyStartEmail;
    this.sendVerifyResumeEmail = sendVerifyResumeEmail;
    this.sendVerifyFinishEmail = sendVerifyFinishEmail;
    this.tourCompleted = tourCompleted;
    this.preferredLanguage = preferredLanguage;
  }
}

export const userConverter = {
  toFirestore: (user: User) => {
    return {
      id: user.id,
      displayName: user.displayName || null,
      role: user.role || null,
      email: user.email || null,
      phoneNumber: user.phoneNumber || null,
      photoURL: user.photoURL || null,
      publiclyVisible: user.publiclyVisible || false,
      canWriteAttributeGroups: user.canWriteAttributeGroups || false,
      rewardPointTotal: user.rewardPointTotal || 0,
      parentMail: user.parentMail || null,
      sendMailNotification: user.sendMailNotification || false,
      sendDailyMailSummary: user.sendDailyMailSummary || false,
      sendWeeklyMailSummary: user.sendWeeklyMailSummary || false,
      sendPrepareStartEmail:
        user.sendPrepareStartEmail ?? user.sendMailNotification ?? false,
      sendPrepareResumeEmail:
        user.sendPrepareResumeEmail ?? user.sendMailNotification ?? false,
      sendPrepareFinishEmail:
        user.sendPrepareFinishEmail ?? user.sendMailNotification ?? false,
      sendStudyStartEmail:
        user.sendStudyStartEmail ?? user.sendMailNotification ?? false,
      sendStudyFinishEmail:
        user.sendStudyFinishEmail ?? user.sendMailNotification ?? false,
      sendVerifyStartEmail:
        user.sendVerifyStartEmail ?? user.sendMailNotification ?? false,
      sendVerifyResumeEmail:
        user.sendVerifyResumeEmail ?? user.sendMailNotification ?? false,
      sendVerifyFinishEmail:
        user.sendVerifyFinishEmail ?? user.sendMailNotification ?? false,
      tourCompleted: user.tourCompleted || false,
      preferredLanguage: user.preferredLanguage || null,
    };
  },
  fromFirestore: (snapshot: DocumentSnapshot, options?: SnapshotOptions) => {
    if (!snapshot.exists()) {
      return undefined;
    }
    const data = snapshot.data(options);
    return new User(
      snapshot.id,
      data['displayName'] ?? undefined,
      data['role'] ?? undefined,
      data['email'] ?? undefined,
      data['phoneNumber'] ?? undefined,
      data['photoURL'] ?? undefined,
      data['publiclyVisible'] ?? false,
      data['rewardPointTotal'] ?? 0,
      data['canWriteAttributeGroups'] ?? false,
      data['parentMail'] ?? undefined,
      data['sendMailNotification'] ?? false,
      data['sendDailyMailSummary'] ?? false,
      data['sendWeeklyMailSummary'] ?? false,
      data['sendPrepareStartEmail'],
      data['sendPrepareResumeEmail'],
      data['sendPrepareFinishEmail'],
      data['sendStudyStartEmail'],
      data['sendStudyFinishEmail'],
      data['sendVerifyStartEmail'],
      data['sendVerifyResumeEmail'],
      data['sendVerifyFinishEmail'],
      data['tourCompleted'] ?? false,
      data['preferredLanguage'] || undefined
    );
  },
  fromFirestore2: (userObj: any | undefined) => {
    if (!userObj) {
      return undefined;
    }
    return new User(
      userObj.id || '',
      userObj.displayName ?? undefined,
      userObj.role ?? undefined,
      userObj.email ?? undefined,
      userObj.phoneNumber ?? undefined,
      userObj.photoURL ?? undefined,
      userObj.publiclyVisible ?? false,
      userObj.rewardPointTotal ?? 0,
      userObj.canWriteAttributeGroups ?? false,
      userObj.parentMail ?? undefined,
      userObj.sendMailNotification ?? false,
      userObj.sendDailyMailSummary ?? false,
      userObj.sendWeeklyMailSummary ?? false,
      userObj.sendPrepareStartEmail,
      userObj.sendPrepareResumeEmail,
      userObj.sendPrepareFinishEmail,
      userObj.sendStudyStartEmail,
      userObj.sendStudyFinishEmail,
      userObj.sendVerifyStartEmail,
      userObj.sendVerifyResumeEmail,
      userObj.sendVerifyFinishEmail,
      userObj.tourCompleted ?? false,
      userObj.preferredLanguage || undefined
    );
  },

  toFirestoreUpdateObject: (obj: {
    id?: string;
    name?: string;
    role?: string;
    email?: string;
    // ...
  }) => {
    const updateObj: any = {};
    Object.keys(obj).forEach((key) => {
      if ((<any>obj)[key] !== undefined) {
        if (
          key === 'role' &&
          !['teacher', 'parent', 'student'].includes((<any>obj)[key])
        ) {
          throw new Error(
            'You are trying to update an user with an invalid role: ' +
              (<any>obj)[key]
          );
        }
        updateObj[key] = (<any>obj)[key];
      }
    });
    return updateObj;
  },
};
